// What's picked and open: a tap or click on a train or a station opens its
// card, a mouse over one shows its label, Follow keeps the open train in
// view, and on a phone the card is a sheet over the bottom of the map that a
// swipe down closes. A city's own trains and stations (London's National
// Rail) are picked, carded and followed through its extension.

import type { Map as MapLibreMap, MapMouseEvent } from 'maplibre-gl';
import { follower } from '@tol/core/follow';
import { STATION_LAYER, STOP_LAYER } from '@tol/core/layers';
import * as Render from '@tol/core/render';
import type { Renderer } from '@tol/core/render';
import { ZOOM_OFFSET } from '@tol/core/view';
import type { MapView } from '@tol/core/view';
import type {
  LatLon,
  LineId,
  PageLine,
  ScreenPoint,
  StationId,
  TrackedTrain,
  TrainId,
  TrainStore,
} from '@tol/core/types';
import { createCards } from './card.ts';
import type { CardHooks } from './card.ts';
import type { Clock } from './clock.ts';
import { el, setLineColor } from './dom.ts';
import type { Selection, ShellState } from './state.ts';

// The station whose dot is under a point.
export interface StationHit {
  readonly id: string;
  readonly name: string;
  readonly lngLat: readonly [number, number];
  // What opens on a click; default: the shell's station card.
  readonly select?: Selection;
}

// A train of the city's own under a point (London's National Rail).
export interface ExtraPick {
  readonly select: Selection;
  readonly distance: number;
  // The hover label.
  readonly color: string;
  readonly text: string;
}

// A city's own trains and stations: picked after the shell's, carded when
// the selection is of its own type, and followed.
export interface SelectionExtension {
  readonly pick?: (pt: ScreenPoint, radius: number) => ExtraPick | null;
  readonly stationAt?: (pt: ScreenPoint, radius: number) => StationHit | null;
  // Renders a selection of its own type; false for one it doesn't know.
  readonly renderCard?: (card: HTMLElement, selection: Selection, full: boolean) => boolean;
  // Where a followed selection of its own type is, for Follow.
  readonly followed?: (selection: Selection) => LatLon | null;
}

export interface SelectionOptions {
  readonly map: MapLibreMap;
  readonly view: MapView;
  readonly renderer: Renderer;
  // The network, the open selection and whether it's followed.
  readonly state: ShellState;
  readonly live: TrainStore;
  readonly lineStyle: (id: LineId) => PageLine;
  readonly hasLine: (id: LineId) => boolean;
  readonly now: () => number;
  readonly clock: Pick<Clock, 'fmtEta' | 'clockTime' | 'clockDate'>;
  readonly stationName: (sid: StationId) => string;
  readonly card: CardHooks;
  readonly extension: SelectionExtension;
}

export interface CardSelection {
  readonly select: (selection: Selection) => void;
  readonly closeCard: () => void;
  readonly renderCard: (full: boolean) => void;
  readonly setFollowing: (on: boolean) => void;
  // The followed train's position, if following.
  readonly followed: () => LatLon | null;
  // Each frame: keep the followed train in view.
  readonly follow: (ts: number) => void;
  // The motion model dropped these trains: the open one's card closes.
  readonly trainsRemoved: (removed: readonly TrainId[]) => void;
  readonly hideHover: () => void;
}

export function createSelection(options: SelectionOptions): CardSelection {
  const { map, view, renderer, state, live, lineStyle, extension } = options;
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');

  // On a phone the card is a sheet over the bottom of the map. Pad the map by
  // its height, so the map's center, and a followed train, stay in view above
  // it, without moving what's on screen when it opens or closes.
  const narrow = window.matchMedia('(max-width: 680px)');
  function sheetPadding(): void {
    const card = el('#card');
    const open = card.hidden !== true;
    document.body.classList.toggle('card-open', open);
    const panelBox = el('#panel').getBoundingClientRect();
    const left = narrow.matches ? 0 : panelBox.right;
    const bottom = narrow.matches ? (open ? card.offsetHeight + 8 : Math.max(0, window.innerHeight - panelBox.top)) : 0;
    const padding = map.getPadding();
    if (Math.abs((padding.bottom ?? 0) - bottom) < 4 && Math.abs((padding.left ?? 0) - left) < 4) return;
    const { clientWidth: w, clientHeight: h } = map.getContainer();
    map.jumpTo({
      center: map.unproject([(w + left) / 2, (h - bottom) / 2]),
      padding: { top: 0, left, right: 0, bottom },
    });
  }
  new MutationObserver(sheetPadding).observe(el('#card'), { attributes: true, attributeFilter: ['hidden'] });
  new ResizeObserver(sheetPadding).observe(el('#card'));
  // Panel text and fonts can resize during a city flight. A padding jump
  // stops that flight, so apply the latest panel size once motion ends.
  new ResizeObserver(() => {
    if (!map.isMoving()) sheetPadding();
  }).observe(el('#panel'));
  map.on('moveend', sheetPadding);
  narrow.addEventListener('change', sheetPadding);
  // A swipe down on the sheet's header closes it. Tapping the map next to it
  // rarely closes anything in a dense city: there's always a station or a
  // train under the finger, and that opens instead.
  {
    const card = el('#card');
    let from: { readonly x: number; readonly y: number } | null = null;
    card.addEventListener(
      'touchstart',
      (e) => {
        const t = e.touches[0];
        const onHeader = e.target instanceof Element && e.target.closest('.card-top, h3, .sub, .handle') !== null;
        from = t && e.touches.length === 1 && onHeader ? { x: t.clientX, y: t.clientY } : null;
      },
      { passive: true },
    );
    card.addEventListener(
      'touchend',
      (e) => {
        const t = e.changedTouches[0];
        const start = from;
        from = null;
        if (!start || !t) return;
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;
        if (dy > 60 && Math.abs(dx) < dy) closeCard();
      },
      { passive: true },
    );
    card.addEventListener(
      'touchcancel',
      () => {
        from = null;
      },
      { passive: true },
    );
  }

  // ---------------------------------------------------------------- hover labels

  const hoverEl = el('#hover');
  const tipEl = el('#station-tip');
  let stationTip: StationHit | null = null;
  function placeStationTip(st: StationHit): void {
    const p = map.project([st.lngLat[0], st.lngLat[1]]);
    const r = Render.stationRadius(view.getZoom(), false);
    tipEl.style.left = `${p.x}px`;
    tipEl.style.top = `${p.y - r - 6}px`;
  }
  function hideHover(): void {
    hoverEl.hidden = true;
    tipEl.hidden = true;
    stationTip = null;
  }
  map.on('move', () => {
    if (state.net && stationTip) placeStationTip(stationTip);
  });

  // ---------------------------------------------------------------- follow

  function followed(): LatLon | null {
    const sel = state.selected;
    if (!state.follow || !sel) return null;
    if (sel.type === 'train') {
      const train = live.trains.get(sel.id);
      return train && !state.hidden.has(train.pred.lineId) ? (train.motion.pos ?? null) : null;
    }
    return extension.followed?.(sel) ?? null;
  }
  const follow = follower(
    {
      isMoving: () => map.isMoving(),
      panTo: (p, { animate, duration }) =>
        map.panTo([p.lon, p.lat], { animate, duration, easing: (x) => x, essential: true }),
    },
    followed,
    {
      get reduceMotion() {
        return motionPreference.matches;
      },
    },
  );

  function trainsRemoved(removed: readonly TrainId[]): void {
    const sel = state.selected;
    if (sel && sel.type === 'train' && removed.includes(sel.id)) closeCard();
  }

  // ---------------------------------------------------------------- pointer

  // A finger covers more than a pointer's tip: a tap reaches further for a
  // station, and takes the nearest of those it reaches.
  let touch = false;
  document.addEventListener(
    'pointerdown',
    (e) => {
      touch = e.pointerType === 'touch' || e.pointerType === 'pen';
    },
    { capture: true, passive: true },
  );
  function stationAt(p: ScreenPoint): StationHit | null {
    const r = touch ? 12 : 3;
    if (!map.getLayer(STATION_LAYER)) return extension.stationAt?.(p, r) ?? null;
    // A station first; a bus stop (its dot is smaller: a wider reach) if none.
    // The nearest of those reached.
    const nearest = (
      layer: string,
      reach: number,
    ): ReturnType<typeof map.queryRenderedFeatures>[number] | undefined => {
      if (!map.getLayer(layer)) return undefined;
      const found = map.queryRenderedFeatures(
        [
          [p.x - reach, p.y - reach],
          [p.x + reach, p.y + reach],
        ],
        { layers: [layer] },
      );
      let best = found[0];
      let bestD = Infinity;
      for (const f of found) {
        if (f.geometry.type !== 'Point') continue;
        const [lon = 0, lat = 0] = f.geometry.coordinates;
        const q = map.project([lon, lat]);
        const d = Math.hypot(q.x - p.x, q.y - p.y);
        if (d < bestD) {
          best = f;
          bestD = d;
        }
      }
      return best;
    };
    const f = nearest(STATION_LAYER, r) ?? nearest(STOP_LAYER, r + 3);
    const props: Readonly<Record<string, unknown>> | undefined = f?.properties;
    const id = props?.['id'];
    const st = typeof id === 'string' ? state.net?.stations.get(id) : undefined;
    return st ? { id: st.id, name: st.name, lngLat: [st.lon, st.lat] } : (extension.stationAt?.(p, r) ?? null);
  }

  // The train nearest a point, the shell's or the city's own, within radius
  // px. Checking the shell's first made a tap on a National Rail train open
  // a TfL train nearby.
  function nearestTrain(
    pt: ScreenPoint,
    radius: number,
  ): { readonly hit: TrackedTrain | null; readonly extra: ExtraPick | null } {
    const hit = renderer.pickTrain(pt, radius);
    const extra = extension.pick?.(pt, radius) ?? null;
    const px = hit?.render.px;
    const distance = px ? Math.hypot(px.x - pt.x, px.y - pt.y) : Infinity;
    if (extra && (!hit || extra.distance < distance)) return { hit: null, extra };
    return { hit, extra: null };
  }

  // A click on a station opens its card, unless a train sits within 9 px of
  // the click; elsewhere, the nearest train within 14 px opens its card; on
  // nothing, the open card closes.
  map.on('click', (e: MapMouseEvent) => {
    const station = stationAt(e.point);
    const { hit, extra } = nearestTrain(e.point, station ? 9 : 14);
    hideHover();
    if (hit) select({ type: 'train', id: hit.id });
    else if (extra) select(extra.select);
    else if (station) select(station.select ?? { type: 'station', id: station.id });
    else closeCard();
  });
  map.on('dragstart', () => {
    if (state.follow) {
      state.follow = false;
      // A refresh, not a rebuild: the list keeps its scroll position.
      renderCard(false);
    }
  });

  // Hover labels are for a mouse. A tap also sends a mousemove, which left a
  // label sitting at the tap point (often half off the screen) on phones.
  let lastPointerWasMouse = true;
  window.addEventListener(
    'pointerdown',
    (e: PointerEvent) => {
      lastPointerWasMouse = e.pointerType === 'mouse';
    },
    true,
  );
  window.addEventListener(
    'pointermove',
    (e: PointerEvent) => {
      if (e.pointerType === 'mouse') lastPointerWasMouse = true;
    },
    true,
  );
  // Next to the pointer, flipped to its left when it would run off the right edge.
  function placeHover(x: number, y: number): void {
    hoverEl.style.left = `${x}px`;
    hoverEl.style.top = `${y}px`;
    hoverEl.classList.toggle('flip', x + 14 + hoverEl.offsetWidth > window.innerWidth - 8);
  }

  let hoverRaf = 0;
  map.on('mousemove', (e: MapMouseEvent) => {
    if (!lastPointerWasMouse) {
      hideHover();
      return;
    }
    if (hoverRaf) return;
    hoverRaf = requestAnimationFrame(() => {
      hoverRaf = 0;
      const { hit, extra } = nearestTrain(e.point, 10);
      const station = hit || extra ? null : stationAt(e.point);
      map.getCanvas().style.cursor = hit || extra || station ? 'pointer' : '';
      if (hit) {
        const l = lineStyle(hit.pred.lineId);
        hoverEl.hidden = false;
        setLineColor(hoverEl, l.show);
        hoverEl.textContent = hit.pred.dest === '' ? l.label : `${l.label} to ${hit.pred.dest}`;
        placeHover(e.originalEvent.clientX, e.originalEvent.clientY);
      } else if (extra) {
        hoverEl.hidden = false;
        setLineColor(hoverEl, extra.color);
        hoverEl.textContent = extra.text;
        placeHover(e.originalEvent.clientX, e.originalEvent.clientY);
      } else hoverEl.hidden = true;
      if (station) {
        if (stationTip?.id !== station.id) tipEl.textContent = station.name;
        stationTip = station;
        tipEl.hidden = false;
        placeStationTip(station);
      } else {
        tipEl.hidden = true;
        stationTip = null;
      }
    });
  });
  map.getCanvas().addEventListener('mouseleave', hideHover);

  // ---------------------------------------------------------------- cards

  function setFollowing(on: boolean): void {
    if (!on && state.follow) map.stop();
    state.follow = on;
    const at = on ? followed() : null;
    if (at) {
      map.stop();
      map.flyTo({
        center: [at.lon, at.lat],
        zoom: Math.max(map.getZoom(), 14 - ZOOM_OFFSET),
        duration: 800,
        animate: !motionPreference.matches,
        essential: true,
      });
    }
  }

  const cards = createCards(options.card, {
    live,
    net: () => state.net,
    lineStyle,
    hasLine: options.hasLine,
    hidden: () => state.hidden,
    now: options.now,
    fmtEta: options.clock.fmtEta,
    clockTime: options.clock.clockTime,
    clockDate: options.clock.clockDate,
    stationName: options.stationName,
    isFollowing: () => state.follow,
    setFollowing,
    close: closeCard,
  });

  function select(selection: Selection): void {
    setFollowing(false);
    state.selected = selection;
    renderCard(true);
  }
  function closeCard(): void {
    setFollowing(false);
    cards.reset();
    state.selected = null;
    el('#card').hidden = true;
  }

  function renderCard(full: boolean): void {
    const card = el('#card');
    const sel = state.selected;
    if (!sel) return;
    if (sel.type === 'train') {
      const t = live.trains.get(sel.id);
      if (!t) {
        closeCard();
        return;
      }
      cards.train(card, t, full);
    } else if (sel.type === 'station') cards.station(card, sel.id, full);
    else if (extension.renderCard?.(card, sel, full) !== true) {
      closeCard();
      return;
    }
    if (state.selected) card.hidden = false;
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeCard();
  });

  return { select, closeCard, renderCard, setFollowing, followed, follow, trainsRemoved, hideHover };
}
